import type {GlobalNavigationCommandId} from '@github-ui/global-navigation-types'
import {Link} from '@github-ui/react-router'
import {getPrimaryKeybinding, GlobalCommands} from '@github-ui/ui-commands'
import type {IconProps} from '@primer/octicons-react'
import {Button, type ButtonProps, IconButton, type IconButtonProps, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import {useRef} from 'react'

import {useGlobalNavAnalytics} from '../utils/Analytics'
import styles from './styles.module.css'

export interface NavLinkItemProps {
  /** Optional DOM id for the rendered control. */
  id?: string

  /** Location this link should be directed to */
  href?: string

  /** The text label for the link */
  linkText?: React.ReactNode

  /** The label for the link (will show as a tooltip) */
  'aria-label': string

  /** The icon to display within the link */
  icon: React.ComponentType<IconProps>

  /** Optional UI Commands command ID to bind to this item. */
  commandId?: GlobalNavigationCommandId

  /** Optional sub-category for analytics */
  analyticsSubCategory?: string

  /** The analytics meta-data to send along when tracking clicks */
  analyticsKey: string | ((event: React.MouseEvent) => string)

  /** Additional class name(s) to apply to the button */
  className?: IconButtonProps['className']

  /** onClick handler */
  onClick?: ButtonProps['onClick']

  /** onMouseEnter handler */
  onMouseEnter?: ButtonProps['onMouseEnter']

  /** onFocus handler */
  onFocus?: ButtonProps['onFocus']

  /** Ref for the anchor element */
  ref?: React.Ref<HTMLAnchorElement>
}

function resolveAnalyticsKey(
  analyticsKey: string | ((event: React.MouseEvent) => string),
  event: React.MouseEvent,
): string {
  return typeof analyticsKey === 'function' ? analyticsKey(event) : analyticsKey
}

/**
 * The NavLinkItem is used to display individual navigation icons in the TopNav
 */
export const NavLinkItem = ({
  id,
  ref: forwardedRef,
  href,
  'aria-label': ariaLabel,
  icon: Icon,
  commandId,
  analyticsKey,
  analyticsSubCategory,
  linkText,
  onClick,
  onMouseEnter,
  onFocus,
  className,
}: NavLinkItemProps) => {
  const {sendItemClick} = useGlobalNavAnalytics(analyticsSubCategory)

  const linkRef = useRef<HTMLAnchorElement>(null)
  const mergedLinkRef = useMergedRefs(forwardedRef ?? null, linkRef)

  if (!href) {
    return (
      <NavButtonItem
        id={id}
        // ref={ref} we can't forward the ref to the button because it expects a link (would need to change the type to `HTMLAnchorElement & HTMLButtonElement`)
        aria-label={ariaLabel}
        icon={Icon}
        analyticsKey={analyticsKey}
        linkText={linkText}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onFocus={onFocus}
        className={className}
        commandId={commandId}
      />
    )
  }

  return (
    <>
      {linkText ? (
        <Button
          id={id}
          ref={mergedLinkRef}
          as={Link}
          to={href}
          variant="invisible"
          aria-label={ariaLabel}
          className={clsx(styles.appHeaderButton, className)}
          onClick={e => {
            sendItemClick(resolveAnalyticsKey(analyticsKey, e))
            onClick?.(e)
          }}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          leadingVisual={Icon}
        >
          {linkText}
        </Button>
      ) : (
        <IconButton
          id={id}
          ref={mergedLinkRef}
          as={Link}
          variant="invisible"
          icon={Icon}
          aria-label={ariaLabel}
          to={href}
          className={clsx(styles.appHeaderButton, className)}
          keybindingHint={commandId && getPrimaryKeybinding(commandId)}
          onClick={e => {
            sendItemClick(resolveAnalyticsKey(analyticsKey, e), ariaLabel)
            onClick?.(e)
          }}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
        />
      )}

      {commandId && <GlobalCommands commands={{[commandId]: () => linkRef.current?.click()}} />}
    </>
  )
}
NavLinkItem.displayName = 'NavLinkItem'

/**
 * The NavButtonItem is used to display individual navigation buttons in the TopNav
 */
export const NavButtonItem = ({
  id,
  'aria-label': ariaLabel,
  icon: Icon,
  commandId,
  analyticsKey,
  linkText,
  onClick,
  onFocus,
  onMouseEnter,
  className,
  ref: forwardedRef,
}: Omit<NavLinkItemProps, 'ref'> & {ref?: React.Ref<HTMLButtonElement>}) => {
  const {sendItemClick} = useGlobalNavAnalytics()

  const ref = useRef<HTMLButtonElement>(null)
  const mergedRef = useMergedRefs(forwardedRef ?? null, ref)

  return (
    <>
      {linkText ? (
        <Button
          id={id}
          ref={mergedRef}
          variant="invisible"
          aria-label={ariaLabel}
          className={clsx(styles.appHeaderButton, className)}
          onClick={e => {
            sendItemClick(resolveAnalyticsKey(analyticsKey, e))
            onClick?.(e)
          }}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
          leadingVisual={Icon}
        >
          {linkText}
        </Button>
      ) : (
        <IconButton
          id={id}
          ref={mergedRef}
          variant="invisible"
          icon={Icon}
          aria-label={ariaLabel}
          className={clsx(styles.appHeaderButton, className)}
          keybindingHint={commandId && getPrimaryKeybinding(commandId)}
          onClick={e => {
            sendItemClick(resolveAnalyticsKey(analyticsKey, e), ariaLabel)
            onClick?.(e)
          }}
          onMouseEnter={onMouseEnter}
          onFocus={onFocus}
        />
      )}

      {commandId && <GlobalCommands commands={{[commandId]: () => ref.current?.click()}} />}
    </>
  )
}
